import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/button';
import { IconFont } from '@/components/iconfont';
import { Markdown } from '@/components/markdown';
import { ModalDialog } from '@/components/modal-dialog';
import { classNames } from '@/utils/ui/classname';
import styles from './confirm.module.scss';

interface IConfirmDialogProps {
  open: boolean;
  /** 有标题时正文一并改为左对齐的灰底块，无标题时正文居中，可由 `boxed` 单独打开 */
  title?: string;
  /**
   * 正文。字符串按 markdown 渲染，ReactNode 原样渲染。
   *
   * ReactNode 只来自业务子组件，不来自全局 confirm 队列——队列项的 content 已
   * 收窄为 string，见 `IConfirmEntry.content`。
   *
   * 为 undefined 时只保留弹窗外壳，用于关闭过程中数据已出队的那一帧。
   */
  content?: string | ReactNode;
  confirmText?: string;
  cancelText?: string;
  /** 只保留主按钮，用于告知而非询问 */
  hideCancel?: boolean;
  /**
   * 正文改用左对齐的灰底块。有标题时必然是灰底块，无标题时缺省为居中纯文本，
   * 只有设计稿明确要灰底块的（账号被停用/删除）才显式打开。
   */
  boxed?: boolean;
  /** 图标与主按钮改用危险色，用于删除这类不可逆动作。缺省是警示色 */
  danger?: boolean;
  /**
   * 确定键转圈、两个按钮一并禁用，用于点完确定还要留在弹窗里等接口结果的场景。
   * 走全局队列的那条路用不上它：队列项点完即出队，弹窗当场就关了。
   */
  confirmLoading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * 警示型对话框的呈现层，不持有任何状态，开关与结果都由调用方决定。
 *
 * 两类调用方共用它，使外观一致而不必复制样式：
 * - 文本（含 markdown 富文本）走全局队列，`hook → store → ConfirmRenderer`；
 * - 需要内嵌交互控件的，从这里派生业务子组件自行持有状态，参考
 *   `DeleteFolderDialog`。队列那条路不行——队列项是快照，受控控件的
 *   value 会永远停在入队那一刻。
 */
export const ConfirmDialog = ({
  open,
  title,
  content,
  confirmText,
  cancelText,
  hideCancel,
  danger,
  boxed,
  confirmLoading = false,
  onConfirm,
  onCancel
}: IConfirmDialogProps) => {
  const { t } = useTranslation();
  const inBox = boxed ?? !!title;

  return (
    <ModalDialog open={open} popupClassName={classNames(styles.dialog, inBox ? undefined : styles.dialogSimple)}>
      {content === undefined ? null : (
        <>
          <div className={styles.content} data-simple={!inBox}>
            {/* 两个 symbol 自带 fill（警示 #ED6A0C、危险 #DF4545），颜色不经 CSS，与主按钮同色 */}
            <IconFont
              iconName={danger ? 'icon-icon_normal_warning_red_fill_24' : 'icon-icon_normal_warning_fill_24'}
              className={styles.warningIcon}
            />
            {title ? <p className={styles.title}>{title}</p> : null}
            {typeof content === 'string' ? (
              <Markdown content={content} className={styles.message} />
            ) : (
              <div className={styles.message}>{content}</div>
            )}
          </div>
          <div className={styles.buttons} data-single={!!hideCancel}>
            {!hideCancel && (
              <Button size="sm" disabled={confirmLoading} onClick={onCancel}>
                {cancelText ?? t('common.cancel')}
              </Button>
            )}
            <Button
              variant="primary"
              className={styles.warningButton}
              size="sm"
              data-danger={!!danger}
              loading={confirmLoading}
              onClick={onConfirm}
            >
              {confirmText ?? t('common.confirm')}
            </Button>
          </div>
        </>
      )}
    </ModalDialog>
  );
};
